import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  QrCode, BarChart3, Link2, Zap, Smartphone, Layers, Sun, Moon, Check,
  ArrowRight, BookOpen, Briefcase, Home, Palette, Users, Sparkles, Shield, Globe, ChevronRight,
  UserCircle, SlidersHorizontal, Share2, CreditCard, Package, Tag, CalendarCheck,
  ScanLine, ExternalLink,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { trackEvent } from "@/lib/tracking";
import { useLanguage } from "@/contexts/LanguageContext";

const BENEFITS = [
  { icon: QrCode, key: "dynamic", accent: "from-indigo-500 to-violet-500" },
  { icon: Smartphone, key: "mobile", accent: "from-cyan-500 to-blue-500" },
  { icon: BarChart3, key: "analytics", accent: "from-emerald-500 to-teal-500" },
  { icon: Layers, key: "variants", accent: "from-orange-500 to-amber-500" },
  { icon: Link2, key: "links", accent: "from-pink-500 to-rose-500" },
  { icon: Zap, key: "vcard", accent: "from-violet-500 to-purple-500" },
  { icon: UserCircle, key: "portrait", accent: "from-fuchsia-500 to-violet-500" },
  { icon: Palette, key: "design", accent: "from-blue-500 to-indigo-500" },
  { icon: CalendarCheck, key: "attendance", accent: "from-teal-500 to-emerald-500" },
];

const SCAN_EXAMPLES = [
  { icon: CreditCard, title: "Business Cards", desc: "Replace static contact info with a dynamic profile link." },
  { icon: BookOpen, title: "Books & Flyers", desc: "Point readers to your latest content, events, or resources." },
  { icon: Package, title: "Product Packaging", desc: "Connect customers to manuals, support, or loyalty programs." },
  { icon: Home, title: "Real Estate Signs", desc: "Each listing links to a detailed property page, updated live." },
  { icon: Tag, title: "Event Badges", desc: "Attendees scan to exchange info and save contacts instantly." },
  { icon: CalendarCheck, title: "Job Sites", desc: "Run GPS-verified attendance from branded site signage." },
];

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.12 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function ShowcaseCard({ item, index }) {
  return (
    <AnimatedSection delay={index * 70}>
      <div className="group relative" data-testid={`showcase-card-${item.id}`}>
        <div className="relative aspect-square rounded-2xl bg-white p-4 border border-border/40 shadow-sm group-hover:shadow-xl group-hover:-translate-y-1.5 transition-all duration-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <img src={item.image} alt={item.name} className="w-full h-full object-contain relative z-10" loading="lazy" data-testid={`showcase-qr-${item.id}`} />
        </div>
        <div className="mt-3 text-center px-1">
          <p className="text-xs font-semibold group-hover:text-primary transition-colors duration-200">{item.name}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{item.desc}</p>
        </div>
      </div>
    </AnimatedSection>
  );
}

export default function LandingPage() {
  const { theme, toggleTheme } = useTheme();
  const { t, formatCurrency } = useLanguage();
  const [settings, setSettings] = useState({});
  const [plans, setPlans] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [demo, setDemo] = useState(null);

  useEffect(() => {
    trackEvent("landing_page_visit");
    Promise.all([
      api.get("/public/settings").then((r) => setSettings(r.data || {})).catch(() => {}),
      api.get("/public/plans").then((r) => setPlans(r.data || [])).catch(() => {}),
      api.get("/public/qr-showcase").then((r) => setShowcase(r.data || [])).catch(() => {}),
      api.get("/public/demo-qr").then((r) => setDemo(r.data || null)).catch(() => {}),
    ]);
  }, []);

  const platformName = settings.platform_name || "Smart-QR";

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="landing-page">
      <SEO
        title="Creative QR Codes, Portrait QR & Smart Attendance"
        description="Create dynamic and portrait QR codes, export print-ready vector artwork, track scans, and manage GPS-verified site attendance from one platform."
        canonicalUrl="https://smart-qr.app/"
        ogUrl="https://smart-qr.app/"
        ogImage="https://smart-qr.app/icon-192.png"
      />
      {/* ─── Nav ─── */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/40">
        <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
              <QrCode className="text-primary-foreground" size={18} />
            </div>
            <span className="font-heading text-lg font-bold tracking-tight">{platformName}</span>
          </div>
          <div className="hidden lg:flex items-center gap-6 ml-auto mr-6 text-xs font-medium text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">{t("landing.features")}</a>
            <a href="#portrait-qr" className="hover:text-foreground transition-colors">{t("landing.portrait")}</a>
            <a href="#attendance" className="hover:text-foreground transition-colors">{t("landing.attendance")}</a>
            <a href="#pricing-section" className="hover:text-foreground transition-colors">{t("landing.pricing")}</a>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2.5 rounded-xl hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" data-testid="landing-theme-toggle">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="nav-login-btn">{t("landing.login")}</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm" className="btn-glow" data-testid="nav-signup-btn">
                <span className="hidden sm:inline">{t("landing.getStarted")}</span>
                <span className="sm:hidden">{t("landing.start")}</span>
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative text-center pt-20 pb-24 md:pt-28 md:pb-36 px-6" data-testid="hero-section">
        <div className="hero-glow" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-b from-primary/[0.03] to-transparent pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mb-6 animate-slide-up">
            {t("landing.heroTitle")}
          </h1>
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-medium animate-slide-up" style={{ animationDelay: "0.05s" }} data-testid="hero-badge">
            <Sparkles size={14} />
            {t("landing.heroBadge")}
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] mb-6 animate-slide-up" style={{ animationDelay: "0.1s" }}>
            {t("landing.heroLine1")}
            <br />
            <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">{t("landing.heroLine2")}</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed animate-slide-up" style={{ animationDelay: "0.2s" }} data-testid="landing-subtitle">
            {t("landing.heroText")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-slide-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/signup" onClick={() => trackEvent("cta_click", { location: "hero" })}>
              <Button size="lg" className="btn-glow px-8 h-12 text-base font-semibold" data-testid="hero-cta-btn">
                {t("landing.create")} <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button variant="outline" size="lg" className="h-12 text-base px-6" data-testid="hero-learn-more-btn">
                {t("landing.how")}
              </Button>
            </a>
          </div>
          <div className="flex items-center justify-center gap-6 mt-10 text-xs text-muted-foreground animate-slide-up" style={{ animationDelay: "0.4s" }}>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> {t("landing.free")}</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> {t("landing.noCard")}</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> {t("landing.fast")}</span>
          </div>
        </div>
      </section>

      {/* ─── Social Proof ─── */}
      <section className="py-10 px-6 border-y border-border/30 bg-secondary/10" data-testid="social-proof-section">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <div className="flex -space-x-3">
                {["bg-gradient-to-br from-indigo-400 to-violet-500", "bg-gradient-to-br from-cyan-400 to-blue-500", "bg-gradient-to-br from-emerald-400 to-teal-500", "bg-gradient-to-br from-orange-400 to-amber-500", "bg-gradient-to-br from-pink-400 to-rose-500"].map((bg, i) => (
                  <div key={i} className={`w-9 h-9 rounded-full ${bg} border-2 border-background flex items-center justify-center shadow-sm`} data-testid={`social-proof-avatar-${i}`}>
                    <span className="text-white text-xs font-bold">{["J","S","M","K","A"][i]}</span>
                  </div>
                ))}
              </div>
              <div className="text-center sm:text-left">
                <p className="text-sm font-medium">{t("landing.trusted")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("landing.join")}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── Benefits ─── */}
      <section id="features" className="py-24 px-6" data-testid="benefits-section">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <p className="text-primary text-sm font-semibold tracking-wide uppercase mb-3">{t("landing.why")}</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold">{t("landing.everything")}</h2>
              <p className="text-muted-foreground text-sm mt-3 max-w-lg mx-auto">{t("landing.everythingText")}</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <AnimatedSection key={b.key} delay={i * 80}>
                <div className="group p-6 rounded-2xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 h-full" data-testid={`benefit-card-${i}`}>
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${b.accent} flex items-center justify-center mb-4 shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                    <b.icon className="text-white" size={20} />
                  </div>
                  <h3 className="font-heading font-semibold text-base mb-2">{t(`benefit.${b.key}.title`)}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t(`benefit.${b.key}.text`)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── New Product Capabilities ─── */}
      <section id="portrait-qr" className="py-24 px-6 bg-secondary/20 border-y border-border/30" data-testid="new-features-section">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary">New in Smart-QR</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold">QR design is no longer one-size-fits-all</h2>
              <p className="text-muted-foreground text-sm sm:text-base mt-4 max-w-2xl mx-auto">Create personal, brand-ready QR artwork and download it for web, print, signage, or production.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AnimatedSection delay={80}>
              <div className="relative overflow-hidden rounded-3xl border border-violet-500/20 bg-card p-7 sm:p-9 h-full" data-testid="portrait-qr-feature">
                <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-violet-500/10 blur-3xl" />
                <div className="relative z-10 grid sm:grid-cols-[1fr_190px] gap-8 items-center">
                  <div>
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20 mb-5">
                      <UserCircle size={23} className="text-white" />
                    </div>
                    <p className="text-xs font-semibold tracking-wide uppercase text-violet-500 mb-2">Portrait QR</p>
                    <h3 className="font-heading text-2xl font-bold mb-3">Turn a headshot into the QR</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">Upload a portrait and Smart-QR integrates it into the QR modules with a protected face focal point, EC-H recovery, and scan-safe finder geometry.</p>
                    <div className="space-y-2 text-xs">
                      {["Adjustable portrait visibility", "Creative gradients and custom QR modules", "Center logo and caption support", "PNG, SVG, PDF, JPEG and WebP"].map((text) => (
                        <div key={text} className="flex items-center gap-2"><Check size={14} className="text-emerald-500 shrink-0" /> {text}</div>
                      ))}
                    </div>
                  </div>
                  <div className="relative mx-auto w-44 h-44 rounded-3xl bg-white border border-border/30 shadow-xl flex items-center justify-center overflow-hidden">
                    <QrCode size={150} className="text-indigo-950" strokeWidth={1.7} />
                    <div className="absolute w-16 h-16 rounded-full bg-white border-[5px] border-white shadow-lg flex items-center justify-center">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-amber-200 via-rose-200 to-violet-300 flex items-center justify-center overflow-hidden">
                        <UserCircle size={48} className="text-violet-900/70" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={160}>
              <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-card p-7 sm:p-9 h-full" data-testid="creative-export-feature">
                <div className="absolute -left-20 -bottom-20 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="relative z-10">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 mb-5">
                    <Palette size={23} className="text-white" />
                  </div>
                  <p className="text-xs font-semibold tracking-wide uppercase text-cyan-600 mb-2">Creative Generator V2</p>
                  <h3 className="font-heading text-2xl font-bold mb-3">Design for screens, print, and production</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">Start with Aurora, Sunset, Ocean, or a classic preset. Then customize colors, gradients, modules, finder eyes, logo placement, and captions.</p>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    {[{ name: "Aurora", bg: "from-teal-600 to-indigo-700" }, { name: "Sunset", bg: "from-pink-700 to-orange-700" }, { name: "Ocean", bg: "from-sky-700 to-blue-950" }].map((style) => (
                      <div key={style.name} className="rounded-2xl border border-border/50 bg-background p-3 text-center">
                        <div className={`w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${style.bg} mb-2 flex items-center justify-center`}><QrCode size={23} className="text-white" /></div>
                        <span className="text-[10px] font-medium">{style.name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["PNG", "SVG Vector", "PDF", "JPEG", "WebP", "Engraving"].map((format) => <Badge key={format} variant="outline" className="text-[10px]">{format}</Badge>)}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={240}>
            <div className="text-center mt-10">
              <Link to="/signup" onClick={() => trackEvent("cta_click", { location: "new_features" })}>
                <Button size="lg" className="btn-glow gap-2">Create a Portrait QR <ArrowRight size={16} /></Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── Smart Attendance ─── */}
      <section id="attendance" className="py-24 px-6" data-testid="attendance-feature-section">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 hover:bg-emerald-500/10">Smart Attendance</Badge>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight mb-5">One site QR for secure worker check-in and check-out</h2>
                <p className="text-muted-foreground leading-relaxed mb-7">Create a site, mint its secure attendance QR, and let workers check in from their phones. Smart Attendance records the event, verifies location when enabled, and keeps the workflow separate from your public Smart-QR profiles.</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: ScanLine, title: "Secure site QR", text: "Revocable, tokenized site access" },
                    { icon: Globe, title: "GPS verification", text: "Optional radius and accuracy controls" },
                    { icon: Users, title: "Live attendance", text: "Worker check-in and check-out status" },
                    { icon: BarChart3, title: "Reports & exports", text: "Filtered records and printable reports" },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3 p-3.5 rounded-xl border border-border/50 bg-card">
                      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0"><item.icon size={17} className="text-emerald-600" /></div>
                      <div><p className="text-xs font-semibold">{item.title}</p><p className="text-[11px] text-muted-foreground mt-0.5">{item.text}</p></div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/signup"><Button className="gap-2">Get Started <ArrowRight size={15} /></Button></Link>
                  <Link to="/contact"><Button variant="outline">Ask About Attendance</Button></Link>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 to-cyan-500/10 rounded-[2rem] blur-2xl" />
                <div className="relative rounded-[2rem] border border-border/50 bg-card shadow-2xl p-5 sm:p-7" data-testid="attendance-dashboard-preview">
                  <div className="flex items-center justify-between mb-6">
                    <div><p className="text-xs text-muted-foreground">Today at</p><p className="font-heading font-bold">St. Paul&apos;s Hospital</p></div>
                    <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/10">Site active</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    {[{ value: "18", label: "On site" }, { value: "24", label: "Check-ins" }, { value: "6", label: "Checked out" }].map((stat) => (
                      <div key={stat.label} className="rounded-xl bg-secondary/40 p-3 text-center"><p className="text-xl font-bold">{stat.value}</p><p className="text-[10px] text-muted-foreground">{stat.label}</p></div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {["Maria S. — Checked in", "John D. — Checked in", "Alex K. — Checked out"].map((row, index) => (
                      <div key={row} className="flex items-center justify-between rounded-xl border border-border/40 p-3">
                        <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"><Users size={14} className="text-primary" /></div><span className="text-xs font-medium">{row}</span></div>
                        <span className={`w-2 h-2 rounded-full ${index === 2 ? "bg-muted-foreground" : "bg-emerald-500"}`} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 p-4 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center border"><QrCode size={42} className="text-emerald-950" /></div>
                    <div><p className="text-xs font-semibold">Branded QR signage</p><p className="text-[11px] text-muted-foreground mt-1">Logo, welcome text, SVG, PNG and PDF export</p></div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section id="how-it-works" className="py-24 px-6 bg-secondary/20 border-y border-border/30" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <p className="text-primary text-sm font-semibold tracking-wide uppercase mb-3">How It Works</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold">Up and running in 3 simple steps</h2>
              <p className="text-muted-foreground text-sm mt-3 max-w-md mx-auto">No coding required. Create your dynamic QR experience in under a minute.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            <AnimatedSection delay={0}>
              <div className="relative flex flex-col items-center text-center" data-testid="how-step-1">
                <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-lg shadow-indigo-500/20 mb-5">
                  <UserCircle size={28} className="text-white" />
                </div>
                <div className="absolute top-3 -left-2 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">Create Your Profile</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">Add your contact details, social links, and media. Your personalized landing page is built automatically.</p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={150}>
              <div className="relative flex flex-col items-center text-center" data-testid="how-step-2">
                <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/20 mb-5">
                  <SlidersHorizontal size={28} className="text-white" />
                </div>
                <div className="absolute top-3 -left-2 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">Customize Your QR</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">Choose a creative preset, add a portrait or logo, customize the design, and configure variants for different audiences.</p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={300}>
              <div className="relative flex flex-col items-center text-center" data-testid="how-step-3">
                <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20 mb-5">
                  <Share2 size={28} className="text-white" />
                </div>
                <div className="absolute top-3 -left-2 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">Share & Track</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">Print or share your QR code anywhere. Track every scan, click, and device in real time.</p>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={450}>
            <div className="text-center mt-14">
              <Link to="/signup" onClick={() => trackEvent("cta_click", { location: "how_it_works" })}>
                <Button size="lg" className="btn-glow px-8 h-11 text-sm font-semibold gap-2" data-testid="how-it-works-cta">
                  Create Your Smart-QR <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── QR Live Demo ─── */}
      {demo?.qr_image && (
        <section className="py-24 px-6" data-testid="live-demo-section">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <p className="text-primary text-sm font-semibold tracking-wide uppercase mb-3">Live Demo</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold">See it in action</h2>
                <p className="text-muted-foreground text-sm mt-3 max-w-md mx-auto">Scan this QR to see a live Smart-QR profile.</p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16">
                {/* QR Code */}
                <div className="shrink-0" data-testid="demo-qr-code">
                  <div className="relative group">
                    <div className="absolute -inset-3 bg-gradient-to-br from-primary/10 to-violet-500/10 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-60" />
                    <div className="relative bg-white rounded-2xl p-5 shadow-lg border border-border/30">
                      <img src={demo.qr_image} alt={demo.profile_name ? `Scannable demo QR code linking to ${demo.profile_name}` : "Scannable demo QR code for Smart-QR"} className="w-48 h-48 sm:w-56 sm:h-56 object-contain" data-testid="demo-qr-image" />
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                      <div className="flex items-center gap-1.5 bg-primary text-primary-foreground text-[10px] font-semibold px-3 py-1 rounded-full shadow-lg">
                        <ScanLine size={11} /> SCAN ME
                      </div>
                    </div>
                  </div>
                </div>

                {/* Demo Info */}
                <div className="text-center md:text-left max-w-sm" data-testid="demo-info">
                  <h3 className="font-heading text-lg font-bold mb-2">{demo.profile_name}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-5">{demo.profile_desc}</p>
                  <div className="space-y-2.5 text-sm text-muted-foreground">
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <Check size={16} className="text-emerald-500 shrink-0" /> Profile picture & bio
                    </div>
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <Check size={16} className="text-emerald-500 shrink-0" /> Social links & website
                    </div>
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <Check size={16} className="text-emerald-500 shrink-0" /> Slide-up animation
                    </div>
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <Check size={16} className="text-emerald-500 shrink-0" /> Save contact / vCard
                    </div>
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <Check size={16} className="text-emerald-500 shrink-0" /> Analytics tracking
                    </div>
                  </div>
                  <div className="mt-6 flex gap-3 justify-center md:justify-start">
                    <a href={demo.profile_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("demo_qr_scan")}>
                      <Button variant="outline" size="sm" className="gap-1.5" data-testid="demo-view-profile-btn">
                        <ExternalLink size={14} /> View Demo Profile
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* ─── QR Style Showcase ─── */}
      {showcase.length > 0 && (
        <section className="py-24 px-6 bg-secondary/20 border-y border-border/30" data-testid="qr-showcase-section">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <p className="text-primary text-sm font-semibold tracking-wide uppercase mb-3">QR Design Presets</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold">{showcase.length} beautiful styles, ready to use</h2>
                <p className="text-muted-foreground text-sm mt-3 max-w-md mx-auto">From Portrait QR and gradients to classic business and laser engraving. Pick the style that fits your brand.</p>
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 sm:gap-6">
              {showcase.map((s, i) => (
                <ShowcaseCard key={s.id} item={s} index={i} />
              ))}
            </div>
            <AnimatedSection delay={showcase.length * 70 + 100}>
              <div className="text-center mt-12">
                <Link to="/signup">
                  <Button variant="outline" size="lg" className="group gap-2" data-testid="showcase-cta-btn">
                    Try All Styles Free <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* ─── Scan Examples (Where to Use) ─── */}
      <section className="py-24 px-6" data-testid="scan-examples-section">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <p className="text-primary text-sm font-semibold tracking-wide uppercase mb-3">Where to Use Smart-QR</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold">Put your QR code anywhere</h2>
              <p className="text-muted-foreground text-sm mt-3 max-w-md mx-auto">From print to signage — your Smart-QR works seamlessly in the physical world.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {SCAN_EXAMPLES.map((ex, i) => (
              <AnimatedSection key={ex.title} delay={i * 80}>
                <div className="group flex flex-col items-center text-center p-5 rounded-2xl border border-border/40 bg-card/60 hover:bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300 h-full" data-testid={`scan-example-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-primary/8 flex items-center justify-center mb-3 group-hover:bg-primary/12 group-hover:scale-105 transition-all duration-300">
                    <ex.icon size={22} className="text-primary" />
                  </div>
                  <h3 className="font-semibold text-xs mb-1">{ex.title}</h3>
                  <p className="text-muted-foreground text-[11px] leading-relaxed">{ex.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust Indicators ─── */}
      <section className="py-16 px-6 border-y border-border/30 bg-secondary/10" data-testid="trust-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <AnimatedSection delay={0}>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><Shield size={20} className="text-emerald-500" /></div>
                <p className="font-semibold text-sm">Secure & Private</p>
                <p className="text-xs text-muted-foreground">Your data is encrypted and never shared with third parties.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center"><Globe size={20} className="text-blue-500" /></div>
                <p className="font-semibold text-sm">Works Everywhere</p>
                <p className="text-xs text-muted-foreground">QR codes scan on any smartphone camera, worldwide.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center"><Zap size={20} className="text-violet-500" /></div>
                <p className="font-semibold text-sm">Lightning Fast</p>
                <p className="text-xs text-muted-foreground">Pages load instantly. Your visitors won't wait.</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      {plans.length > 0 && (
        <section className="py-24 px-6" data-testid="pricing-section">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <p className="text-primary text-sm font-semibold tracking-wide uppercase mb-3">Pricing</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold">Simple, transparent pricing</h2>
                <p className="text-muted-foreground text-sm mt-3">Start free. Upgrade when you're ready.</p>
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {plans.map((plan, i) => {
                const isPro = plan.slug === "pro";
                return (
                  <AnimatedSection key={plan.id} delay={i * 100}>
                    <Card className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full ${isPro ? "border-primary shadow-xl shadow-primary/10 ring-1 ring-primary/20 scale-[1.02]" : "border-border/50 hover:border-primary/20"}`} data-testid={`pricing-card-${plan.slug}`}>
                      {isPro && (
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-violet-500 to-primary" />
                      )}
                      <CardContent className="pt-7 pb-6 px-6">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-bold text-lg">{plan.name}</h3>
                          {isPro && <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20">Popular</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mb-5">{plan.description}</p>
                        <div className="mb-6">
                          <span className="text-4xl font-heading font-extrabold">{formatCurrency(plan.monthly_price, plan.currency || "USD")}</span>
                          {plan.monthly_price > 0 && <span className="text-muted-foreground text-sm">/mo</span>}
                        </div>
                        <Link to="/signup">
                          <Button className={`w-full h-11 ${isPro ? "btn-glow" : ""}`} variant={isPro ? "default" : "outline"} data-testid={`pricing-cta-${plan.slug}`}>
                            {plan.monthly_price === 0 ? "Get Started Free" : "Start Free Trial"}
                          </Button>
                        </Link>
                        <ul className="mt-6 space-y-2.5">
                          {plan.features?.map((f) => (
                            <li key={f} className="flex items-start gap-2.5 text-sm">
                              <Check size={16} className="text-primary shrink-0 mt-0.5" />
                              <span className="text-muted-foreground text-xs">{f}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </AnimatedSection>
                );
              })}
            </div>
            {/* After-pricing CTA */}
            <AnimatedSection delay={plans.length * 100 + 100}>
              <div className="text-center mt-12">
                <Link to="/signup" onClick={() => trackEvent("cta_click", { location: "after_pricing" })}>
                  <Button size="lg" className="btn-glow px-8 h-11 text-sm font-semibold gap-2" data-testid="after-pricing-cta">
                    Start for Free <ArrowRight size={16} />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* ─── Final CTA ─── */}
      <section className="py-28 px-6 bg-secondary/20 border-t border-border/30" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <AnimatedSection>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center mx-auto mb-8 shadow-xl shadow-primary/20">
              <QrCode size={32} className="text-white" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">Ready to create your Smart-QR?</h2>
            <p className="text-muted-foreground mb-10 max-w-md mx-auto leading-relaxed">
              Create a dynamic or Portrait QR, export it for any medium, and add Smart Attendance when your team needs site check-in.
            </p>
            <Link to="/signup" onClick={() => trackEvent("cta_click", { location: "bottom" })}>
              <Button size="lg" className="btn-glow px-10 h-12 text-base font-semibold" data-testid="bottom-cta-btn">
                Create Your Smart-QR <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-border/40 py-10 px-6" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-primary/20 flex items-center justify-center">
                  <QrCode size={12} className="text-primary" />
                </div>
                <span className="font-heading font-semibold text-sm">{platformName}</span>
              </div>
              <Link to="/signup">
                <Button variant="ghost" size="sm" className="text-xs gap-1.5 text-muted-foreground hover:text-primary" data-testid="footer-cta-btn">
                  Create Your Smart-QR <ArrowRight size={12} />
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <a href="#pricing-section" className="hover:text-foreground transition-colors" data-testid="footer-link-pricing">Pricing</a>
              <a href="#portrait-qr" className="hover:text-foreground transition-colors">Portrait QR</a>
              <a href="#attendance" className="hover:text-foreground transition-colors">Smart Attendance</a>
              <Link to="/login" className="hover:text-foreground transition-colors" data-testid="footer-link-login">Login</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors" data-testid="footer-link-terms">Terms of Service</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors" data-testid="footer-link-contact">Contact</Link>
            </div>
            <p className="text-muted-foreground/50 text-[11px] text-center sm:text-left">One QR Code. Infinite Updates.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
